import { useEffect, useMemo, useRef, useState } from 'react';
import {
  groupNewsSourcesByCountry,
  newsSourceToPinnedFeed,
  type NewsSource,
} from '@tsr/shared-types';
import { usePinnedFeeds } from './pinnedFeeds';
import { showBarCaptions } from './lib/commandBarLayout';
import { useCommandBarTier } from './lib/commandBarTier';

/**
 * NEWS menu (#182 — the operator entry point for the YouTube news reel).
 *
 * A command-bar dropdown listing the curated news outlets, grouped by country and
 * filterable by name, each a pin/unpin toggle. Pinning routes through the existing
 * `kind:'news'` pinned-feed path (personal workstation state — localStorage +
 * BroadcastChannel, NO operator_actions verb, NO migration), so a pinned outlet
 * appears in the operations-panel media region and the WALL mosaic, mirroring a
 * camera pin exactly.
 *
 * News is AMBIENT + operator-gated: this is the only place it surfaces, it never
 * touches the map / act-register, and there is no urgency treatment here.
 * Self-contained — owns its pin state via `usePinnedFeeds`, so it needs no props
 * and drops into the command bar without threading state through App.
 *
 * `variant='row'` renders it as a Settings ▾ menu row instead of a bar button —
 * the demotion target when the map region is too narrow for the media menus
 * (commandBarLayout.ts overflow tier); the popover then opens to the LEFT of
 * the right-edge-hugging settings menu.
 */
export function NewsMenu({ variant = 'bar' }: { variant?: 'bar' | 'row' } = {}): JSX.Element {
  const { pins, toggle, isPinned } = usePinnedFeeds();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const captions = showBarCaptions(useCommandBarTier());

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const groups = useMemo(
    () => groupNewsSourcesByCountry(filter.trim() ? { outlet: filter } : {}),
    [filter],
  );
  const pinnedNewsCount = pins.filter((p) => p.kind === 'news').length;

  const togglePin = (source: NewsSource): void => {
    toggle(newsSourceToPinnedFeed(source));
  };

  return (
    <div
      ref={wrapRef}
      className={variant === 'row' ? 'relative' : 'relative flex shrink-0 items-center'}
      data-testid="news-controls"
    >
      {variant === 'row' ? (
        <button
          type="button"
          data-testid="news-menu-btn"
          onClick={() => setOpen((v) => !v)}
          title="News — pin a live news outlet to your workstation"
          aria-haspopup="menu"
          aria-expanded={open}
          className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-800"
        >
          <span>News</span>
          <span className="text-slate-500" aria-hidden>
            {pinnedNewsCount > 0 ? `${pinnedNewsCount} ◂` : '◂'}
          </span>
        </button>
      ) : (
        <button
          type="button"
          data-testid="news-menu-btn"
          onClick={() => setOpen((v) => !v)}
          title="News — pin a live news outlet to your workstation"
          aria-haspopup="menu"
          aria-expanded={open}
          className="flex h-7 items-center gap-1 rounded border border-slate-600 bg-slate-800 px-1.5 text-[11px] text-slate-300 hover:bg-slate-700"
        >
          {captions ? (
            <span>News</span>
          ) : (
            <span aria-hidden>
              {/* broadcast/news glyph for the compact bar */}
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 9h12v8H4zM16 11l4-2v6l-4-2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          )}
          <span aria-hidden>▾</span>
          {pinnedNewsCount > 0 && (
            <span className="rounded bg-slate-700 px-1 text-[10px] text-slate-300">{pinnedNewsCount}</span>
          )}
        </button>
      )}
      {open && (
        <div
          data-testid="news-menu"
          role="menu"
          className={`absolute z-50 w-72 rounded-lg border border-slate-700 bg-slate-900/95 p-1 shadow-lg ${
            variant === 'row' ? 'right-full top-0 mr-1' : 'left-0 top-full mt-1'
          }`}
        >
          <div className="px-1 pb-1">
            <input
              data-testid="news-filter-input"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter outlets…"
              className="w-full rounded border border-slate-600 bg-slate-800 px-1.5 py-1 text-xs text-slate-100 placeholder:text-slate-500"
            />
          </div>
          <p className="px-2 pb-1 text-[10px] text-slate-500">
            Ambient live news — pinned to this workstation only.
          </p>
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {groups.length === 0 ? (
              <p className="px-2 py-1.5 text-[11px] text-slate-500">No outlets match.</p>
            ) : (
              groups.map((g) => (
                <div key={g.country}>
                  <div className="px-2 pt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    {g.countryLabel}
                  </div>
                  <ul className="space-y-0.5">
                    {g.sources.map((s) => {
                      const pinned = isPinned('news', s.id);
                      return (
                        <li key={s.id}>
                          <button
                            type="button"
                            data-testid="news-source-toggle"
                            data-pinned={pinned ? 'true' : 'false'}
                            onClick={() => togglePin(s)}
                            className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-800"
                            title={pinned ? `Unpin ${s.outlet}` : `Pin ${s.outlet} to this workstation`}
                          >
                            <span className="truncate">{s.outlet}</span>
                            <span
                              className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                                pinned
                                  ? 'bg-sky-700/70 text-sky-100'
                                  : 'border border-slate-600 text-slate-400'
                              }`}
                            >
                              {pinned ? 'Pinned' : 'Pin'}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
